// <Photo> — a thin wrapper around <img> that signs the URL on demand for
// private storage buckets. Accepts either a clean storage path or a legacy
// public-URL value (DB rows from before bucket privacy may still hold those).
//
// Use:
//   <Photo bucket="delivery-photos" value={pallet.scale_photo_url}
//          alt="Scale" className="w-full max-h-32 object-cover rounded-xl" />
//
// By default wraps in an <a target="_blank"> so the image opens full-size.
// Pass linkWrap={false} to render bare <img>.

import { useSignedUrl } from '@/lib/storage'

interface PhotoProps {
  bucket:    string
  value:     string | null | undefined
  alt:       string
  className?: string
  linkWrap?: boolean              // wrap in <a> (default true)
  fallback?: React.ReactNode      // shown while loading or if signing fails
}

export default function Photo({
  bucket, value, alt, className, linkWrap = true, fallback = null,
}: PhotoProps) {
  const { data: url, isLoading } = useSignedUrl(bucket, value)

  if (!value) return <>{fallback}</>
  if (isLoading) {
    // Render a placeholder of the same shape so layout doesn't shift.
    return (
      <div className={className} aria-label={`Loading ${alt}`} role="img"
        style={{ background: 'rgba(75, 85, 99, 0.3)' }} />
    )
  }
  if (!url) return <>{fallback}</>

  // Off-screen images wait (F072): a 1 000-card register used to download
  // every full-size photo before the reader had scrolled past the first week.
  const img = <img src={url} alt={alt} className={className} loading="lazy" />
  if (!linkWrap) return img
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {img}
    </a>
  )
}
